import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/auth';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const { login, isLoading, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  // Get success message from navigation state (e.g., from password reset)
  const successMessage = location.state?.message;

  // Simple form initialization without complex state management
  useEffect(() => {
    // Ensure inputs are focusable after mount (defensive programming)
    const timer = setTimeout(() => {
      if (emailInputRef.current) {
        // Just ensure the input is properly initialized
        emailInputRef.current.removeAttribute('disabled');
      }
      if (passwordInputRef.current) {
        passwordInputRef.current.removeAttribute('disabled');
      }
    }, 0);

    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Simple validation without complex state checks
    if (!email.trim() || !password.trim()) {
      console.warn('Email and password are required');
      return;
    }
    
    try {
      await login({ email, password });
      navigate('/dashboard');
    } catch (err) {
      // Error is handled by the auth context
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!forgotPasswordEmail.trim()) {
      setForgotPasswordError('Please enter your email address');
      return;
    }

    setForgotPasswordLoading(true);
    setForgotPasswordError('');
    setForgotPasswordMessage('');

    try {
      await authService.forgotPassword(forgotPasswordEmail);
      setForgotPasswordMessage('If an account exists with this email, a password reset link has been sent.');
      setForgotPasswordEmail('');
    } catch (err) {
      setForgotPasswordError(err instanceof Error ? err.message : 'Failed to send reset email');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleShowForgotPassword = () => {
    setShowForgotPassword(true);
    setForgotPasswordEmail(email); // Pre-fill with login email if available
    setForgotPasswordError('');
    setForgotPasswordMessage('');
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setForgotPasswordEmail('');
    setForgotPasswordError('');
    setForgotPasswordMessage('');
  };

  return (
    <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div className="card" style={{ width: '100%', maxWidth: '400px' }}>
        <div className="card-header">
          <h2 className="card-title">Sign In</h2>
          <p style={{ color: '#666', margin: 0 }}>
            Access your Medical Device Cybersecurity Guide
          </p>
        </div>

        {successMessage && (
          <div className="alert alert-success">
            {successMessage}
          </div>
        )}

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {!showForgotPassword ? (
          <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email Address
            </label>
            <input
              ref={emailInputRef}
              type="email"
              id="email"
              className="form-input"
              value={email}
              onChange={handleEmailChange}
              required
              disabled={isLoading}
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <input
              ref={passwordInputRef}
              type="password"
              id="password"
              className="form-input"
              value={password}
              onChange={handlePasswordChange}
              required
              disabled={isLoading}
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner" style={{ marginRight: '0.5rem' }}></span>
                Signing In...
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>
        ) : (
          <div>
            <div style={{ marginBottom: '1rem' }}>
              <button
                type="button"
                onClick={handleBackToLogin}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: '#007bff', 
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  padding: 0
                }}
              >
                ← Back to Sign In
              </button>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#333' }}>Reset Password</h3>
              <p style={{ color: '#666', margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
                Enter your email address and we'll send you a link to reset your password.
              </p>
            </div>

            {forgotPasswordError && (
              <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
                {forgotPasswordError}
              </div>
            )}

            {forgotPasswordMessage && (
              <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
                {forgotPasswordMessage}
              </div>
            )}

            <form onSubmit={handleForgotPassword}>
              <div className="form-group">
                <label htmlFor="forgotPasswordEmail" className="form-label">
                  Email Address
                </label>
                <input
                  type="email"
                  id="forgotPasswordEmail"
                  className="form-input"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  required
                  disabled={forgotPasswordLoading}
                  autoComplete="email"
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                style={{ width: '100%' }}
                disabled={forgotPasswordLoading}
              >
                {forgotPasswordLoading ? (
                  <>
                    <span className="spinner" style={{ marginRight: '0.5rem' }}></span>
                    Sending Reset Link...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </button>
            </form>
          </div>
        )}

        {!showForgotPassword && (
          <>
            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <button
                type="button"
                onClick={handleShowForgotPassword}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: '#007bff', 
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  textDecoration: 'underline'
                }}
              >
                Forgot your password?
              </button>
            </div>

            <div style={{ textAlign: 'center', marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e1e5e9' }}>
              <p style={{ color: '#666', marginBottom: '0.5rem' }}>
                Don't have an account?
              </p>
              <Link to="/register" className="btn btn-outline" style={{ width: '100%' }}>
                Create Free Account
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
